import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Download } from "lucide-react";
import { exportToCsv } from "@/lib/exportCsv";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeesDetailDialog({ open, onOpenChange }: Props) {
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["dashboard-fees-detail"],
    queryFn: async () => {
      const { data: invData } = await supabase
        .from("invoices")
        .select("id, student_id, amount, due_date, status, description")
        .eq("status", "unpaid")
        .order("due_date", { ascending: true });

      if (!invData?.length) return [];

      const studentIds = [...new Set(invData.map((i) => i.student_id))];
      const { data: students } = await supabase.from("students").select("id, first_name, last_name, student_number").in("id", studentIds);
      const studentMap = new Map((students ?? []).map((s) => [s.id, `${s.first_name} ${s.last_name} (${s.student_number})`]));

      return invData.map((inv) => ({
        ...inv,
        student_name: studentMap.get(inv.student_id) ?? "Unknown",
        is_overdue: inv.due_date ? new Date(inv.due_date) < new Date() : false,
      }));
    },
    enabled: open,
  });

  const overdue = invoices.filter((i) => i.is_overdue);
  const unpaidNotOverdue = invoices.filter((i) => !i.is_overdue);

  const handleExport = () => {
    const headers = ["Student", "Description", "Amount", "Due Date", "Overdue"];
    const rows = invoices.map((inv) => [inv.student_name, inv.description ?? "", String(inv.amount), inv.due_date ?? "", inv.is_overdue ? "Yes" : "No"]);
    exportToCsv("outstanding-fees.csv", headers, rows);
  };

  const renderList = (items: typeof invoices) =>
    items.length === 0 ? (
      <p className="text-sm text-muted-foreground text-center py-6">No invoices</p>
    ) : (
      <div className="space-y-2 max-h-[350px] overflow-y-auto">
        {items.map((inv) => (
          <div key={inv.id} className="flex items-center justify-between p-3 border rounded-lg">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{inv.student_name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {inv.description ?? "Invoice"} · {inv.due_date ? format(new Date(inv.due_date), "dd MMM yyyy") : "No due date"}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm font-semibold">${Number(inv.amount).toLocaleString()}</span>
              {inv.is_overdue && <Badge variant="destructive">Overdue</Badge>}
            </div>
          </div>
        ))}
      </div>
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="flex flex-row items-center justify-between gap-2">
          <div>
            <DialogTitle>Outstanding Fees</DialogTitle>
            <DialogDescription>Unpaid invoices breakdown</DialogDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={invoices.length === 0} className="mr-8 shrink-0">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground text-center py-6">Loading…</p>
        ) : (
          <Tabs defaultValue="overdue">
            <TabsList className="w-full">
              <TabsTrigger value="overdue" className="flex-1">Overdue ({overdue.length})</TabsTrigger>
              <TabsTrigger value="unpaid" className="flex-1">Unpaid ({unpaidNotOverdue.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="overdue">{renderList(overdue)}</TabsContent>
            <TabsContent value="unpaid">{renderList(unpaidNotOverdue)}</TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
